import React, { useEffect, useRef, useState } from 'react';
import { FileCheck2 } from 'lucide-react';
import {
  LegalDocument,
  acceptLegalDocument,
  fetchPendingLegalAcceptances,
} from '../services/legalDocuments';
import { LEGAL_ACCEPTANCE_REQUIRED_EVENT } from '../services/auth';
import { LegalMarkdownContent, formatEffectiveDate } from './LegalDocumentContent';
import './LegalAcceptanceModal.css';

interface LegalAcceptanceModalProps {
  enabled: boolean;
  onDecline: () => void;
}

const LegalAcceptanceModal: React.FC<LegalAcceptanceModalProps> = ({ enabled, onDecline }) => {
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [refreshRequest, setRefreshRequest] = useState(0);
  const documentRef = useRef<HTMLDivElement>(null);
  const activeDocument = documents[0];

  useEffect(() => {
    if (!enabled) return;
    const handleAcceptanceRequired = () => setRefreshRequest((current) => current + 1);
    window.addEventListener(LEGAL_ACCEPTANCE_REQUIRED_EVENT, handleAcceptanceRequired);
    return () => window.removeEventListener(
      LEGAL_ACCEPTANCE_REQUIRED_EVENT,
      handleAcceptanceRequired,
    );
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setDocuments([]);
      return;
    }

    let active = true;
    setLoading(true);
    fetchPendingLegalAcceptances()
      .then((pending) => {
        if (active) setDocuments(pending);
      })
      .catch((requestError) => {
        console.error('Error loading pending legal acceptances:', requestError);
        if (active) setError('Nu am putut verifica documentele legale. Reîncearcă.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [enabled, refreshRequest]);

  useEffect(() => {
    if (!activeDocument) return;
    if (documentRef.current) {
      documentRef.current.scrollTop = 0;
    }
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    return () => {
      window.document.body.style.overflow = previousOverflow;
    };
  }, [activeDocument]);

  if (!enabled || loading || !activeDocument) return null;

  const handleAccept = async () => {
    setAccepting(true);
    setError('');
    try {
      await acceptLegalDocument(activeDocument);
      if (documents.length === 1) {
        window.location.reload();
        return;
      }
      setDocuments((current) => current.slice(1));
    } catch (requestError) {
      console.error('Error accepting legal document:', requestError);
      setError('Nu am putut înregistra acceptarea. Reîncearcă.');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="legal-acceptance-overlay">
      <section className="legal-acceptance-dialog" role="dialog" aria-modal="true" aria-labelledby="legal-acceptance-title">
        <header>
          <FileCheck2 aria-hidden />
          <div>
            <h2 id="legal-acceptance-title">Actualizare document legal</h2>
            <p>Pentru a continua, citește și acceptă versiunea actualizată.</p>
          </div>
        </header>

        <div ref={documentRef} className="legal-acceptance-document legal-container" tabIndex={0}>
          <h1 className="legal-title">{activeDocument.title}</h1>
          <p className="legal-update">Data intrării în vigoare: {formatEffectiveDate(activeDocument)}</p>
          <LegalMarkdownContent content={activeDocument.content} />
        </div>

        {error && <p className="legal-acceptance-error" role="alert">{error}</p>}
        <footer>
          <button type="button" className="legal-acceptance-decline" disabled={accepting} onClick={onDecline}>Refuz și mă deconectez</button>
          <button type="button" className="legal-acceptance-confirm" disabled={accepting} onClick={() => void handleAccept()}>
            {accepting ? 'Se salvează...' : 'Accept și continui'}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default LegalAcceptanceModal;
